import { ReactElement, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { filter, find } from 'lodash-es'
import { v4 } from 'uuid'
import { TabContext, TabPanel, TabList } from '@mui/lab'
import { Box, Stack, styled, Tab } from '@mui/material'
import { WallSettings } from '@editor/pages/StructuralPlanning/components/PlanSettingsForm/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import {
  useEditElementStore,
  useModelStore,
  useStiffeningStore,
  useTransmissionStore,
} from '@editorStores'
import { useBlockScene, useResultsInvalidation } from '@editorHooks'
import {
  AssemblyAssignmentForm,
  CollapsibleAssemblyBoxSection,
  CollapsibleCopyBoxSection,
  CollapsibleDocumentationBoxSection,
  CollapsibleOpeningsBoxSection,
  CollapsibleProblemsBoxSection,
  CollapsibleSettingsBoxSection,
  CollapsibleSplitBoxSection,
  CollapsibleStiffeningBoxSection,
  CollapsibleWallRipsBoxSection,
  DocumentationItem,
} from '@editorComponents'
import {
  getElementLoads,
  getModel,
  getProjectFiles,
  getStiffeningIntervals,
  getStiffeningProposal,
  getTensileTransmissionGraph,
  getVerticalTransmissionGraph,
} from '@queries'
import {
  saveStiffeningIntervals,
  recalculateTensileTargets,
  deleteWall,
  createWall,
  updateWall as updateWallRequest,
} from '@mutations'
import MultiEditWallRipsEdges from 'src/components/pages/Editor/components/MultiEditWallRipsEdges'
import { documentationRootGuid } from 'src/constants'
import { buildErrorMessage } from 'src/constants/errors'
import { useStructuralPlanningSelection, useModelInvalidation } from '../../hooks'
import { useSaveTensileTransmissionGraph } from '../../hooks/useSaveTensileTransmissionGraph'
import CopyToOtherStoreys from '../MultiEdit/components/CopyToOtherStoreys'
import PlanSettingsForm from '../PlanSettingsForm'
import SplitWallForm from '../SplitWallForm'
import StiffeningElementList from '../StiffeningElementList'
import TensileTransmitterList from '../TensileTransmitterList'
import VerticalTransmitterList from '../VerticalTransmitterList'
import WallGeometryForm from '../WallGeometryForm'
import { toNewWallPosition } from '../WallGeometryForm/utils'
import WallOpeningsSection from '../WallOpeningsSection'
import { ElementFormMode } from '../elementFormMode'
import ConfiguredWallAlignmentForm from './components/ConfiguredWallAlignmentForm'
import { wallTabs } from './tabs'

const StyledTabPanel = styled(TabPanel)({
  paddingLeft: 8,
  paddingRight: 8,
  paddingTop: 8,
  paddingBottom: 0,
})

interface Props {
  elementType: ElementTypes
  selectedElementGuid: string
  onClose: () => void
  initialTabValue: string
  /**
   * Hide the tab headers and show only the "Bauteil" (geometry) tab content. Used where the other
   * tabs are not relevant (e.g. the IFC import planar-model editing drawer).
   */
  hideTabs?: boolean
  /**
   * `architectural` hides the structural-only sections (Aufbau, Wandrippen, Dokumentation,
   * Probleme) — used by the IFC importer. Defaults to `all`, which shows every section.
   */
  mode?: ElementFormMode
  /** Passed through to `WallOpeningsSection` — see its own doc comment. */
  onEditOpeningInTopView?: (wallGuid: string) => void
  /** edit the wall in the scene as a 2D line (the IFC importer's orthographic view) */
  sceneEditEnabled?: boolean
  /** save right after every move/extension in the scene */
  saveOnSceneEdit?: boolean
}

const WallForm = ({
  elementType,
  selectedElementGuid,
  initialTabValue,
  onClose,
  hideTabs = false,
  mode = 'all',
  onEditOpeningInTopView,
  sceneEditEnabled = false,
  saveOnSceneEdit = false,
}: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const { projectId }: { projectId?: string } = useParams()
  const queryClient = useQueryClient()

  const {
    params: { selectedStiffeningElements },
    actions: { setStiffeningElement },
  } = useStructuralPlanningSelection()

  const invalidateResults = useResultsInvalidation()
  const invalidateModel = useModelInvalidation()

  const model = useModelStore(state => state.model)

  const {
    params: { activeTab, selectedElements, verticalTransmitter },
    actions: { setActiveTab, setVerticalTransmitter, reset },
  } = useStructuralPlanningSelection()
  const setTensileTransmissionGraph = useTransmissionStore(
    state => state.setTensileTransmissionGraph,
  )

  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)

  const removeWall = useModelStore(state => state.removeWall)

  const setActiveElement = useEditElementStore(state => state.setActiveElement)

  const wall = useMemo(
    () => find(model.walls, { guid: selectedElementGuid }),
    [model, selectedElementGuid],
  )

  // Derived from the live wall rather than the selection, so a placement change (internal <->
  // external) immediately offers the matching assemblies and shows the newly assigned one
  const assemblyElementType: ElementTypes = useMemo(() => {
    if (!wall?.placement) return elementType
    return wall.placement === 'Internal' ? 'inner_walls' : 'outer_walls'
  }, [wall, elementType])

  const intervals = useMemo(() => {
    return filter(stiffeningIntervals, ['element_guid', selectedElementGuid])
  }, [stiffeningIntervals, selectedElementGuid])

  const { mutate: saveGraph, isLoading: isSavingGraph } = useSaveTensileTransmissionGraph(
    projectId as string,
  )

  const { mutate: recalculateTargets, isLoading: isRecalculating } = useMutation(
    () => recalculateTensileTargets.request(projectId, selectedElements?.[0] as string),
    {
      onSuccess: async data => {
        setTensileTransmissionGraph(data)
        invalidateResults(projectId as string)
        enqueueSnackbar('Ziele erfolgreich neu berechnet', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim der Berechnung der Ziele'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutate: saveIntervals, isLoading: isSavingIntervals } = useMutation(
    (mergedProposal: StiffeningProposal) =>
      saveStiffeningIntervals.request(projectId, mergedProposal),
    {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(getStiffeningIntervals.getKey(projectId)),
          queryClient.invalidateQueries(getTensileTransmissionGraph.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Aussteifende Elemente erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(
          buildErrorMessage(error, 'Fehler beim speichern der Aussteifenden Elemente'),
          { variant: 'error' },
        )
      },
    },
  )

  const { mutateAsync: handleDelete, isLoading: isDeleting } = useMutation(
    (wallGuid: string) => {
      return deleteWall.request(projectId, wallGuid)
    },
    {
      onSuccess: async () => {
        setActiveElement(null)
        removeWall(selectedElementGuid)
        await Promise.all([
          queryClient.invalidateQueries(getModel.getKey(projectId)),
          queryClient.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          queryClient.invalidateQueries(getModel.getKey(projectId)),
          queryClient.invalidateQueries(getStiffeningProposal.getKey(projectId)),
          queryClient.invalidateQueries(getStiffeningIntervals.getKey(projectId)),
          queryClient.invalidateQueries(getTensileTransmissionGraph.getKey(projectId)),
          queryClient.invalidateQueries(getProjectFiles.getKey(projectId, documentationRootGuid)),
        ])

        invalidateResults(projectId as string)
        enqueueSnackbar('Wand erfolgreich gelöscht', { variant: 'success' })
      },
      onError: (error: Error | AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen der Wand'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: handleUpdate, isLoading: isUpdating } = useMutation(
    async ({
      submittedWall,
      dependentWalls,
    }: {
      submittedWall: ShapeObject
      dependentWalls: ShapeObject[]
      /** saved right after a move in the scene: keep the selection, no success message */
      fromSceneEdit?: boolean
    }) => {
      await updateWallRequest.request(
        projectId as string,
        submittedWall.guid,
        toNewWallPosition(submittedWall),
      )
      for (const dependentWall of dependentWalls) {
        await updateWallRequest.request(
          projectId as string,
          dependentWall.guid,
          toNewWallPosition(dependentWall),
        )
      }
      return submittedWall
    },
    {
      mutationKey: ['updateWall', projectId],
      onSuccess: async (_submittedWall, { fromSceneEdit }) => {
        if (!fromSceneEdit) reset()
        await invalidateModel(projectId as string)
        await queryClient.invalidateQueries(getElementLoads.getKey(projectId))
        await queryClient.invalidateQueries(getModel.getKey(projectId))
        invalidateResults(projectId as string)
        if (!fromSceneEdit) enqueueSnackbar('Wand erfolgreich verändert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Ändern der Wand'), {
          variant: 'error',
        })
        // the main wall may have been saved before a dependent wall failed: restore the server state
        invalidateModel(projectId as string)
      },
    },
  )

  // no further scene edits while one is being saved, they would be based on the outdated wall
  useBlockScene(saveOnSceneEdit && isUpdating)

  const selectedTab = useMemo(() => {
    if (hideTabs) return wallTabs.geometry.tab

    if (activeTab && find(Object.entries(wallTabs), ([, e]) => e.tab === activeTab))
      return activeTab

    return initialTabValue
  }, [activeTab, initialTabValue, hideTabs])

  const wallLength = useMemo(() => {
    if (!wall) return
    return wall.shape.points[0].distanceTo(wall.shape.points[1])
  }, [wall])

  // The architectural mode (IFC importer) hides the structural-only sections.
  const showStructuralSections = mode === 'all'

  const handleCreateWall = async (
    wallData: {
      p1: { x: number; y: number }
      p2: { x: number; y: number }
      storey_idx: number
      placement: WallPlacement
      openings?: Opening[]
    } & WallSettings,
  ) => {
    const newWall = await createWall.request(projectId as string, {
      guid: v4(),
      p1: wallData.p1,
      p2: wallData.p2,
      storey_idx: wallData.storey_idx,
      placement: wallData.placement as 'Internal',
      positionPlanSettings: wallData.positionPlanSettings,
      certificateSettings: wallData.certificateSettings,
    })
    await Promise.all([
      queryClient.invalidateQueries(getModel.getKey(projectId)),
      queryClient.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
      queryClient.invalidateQueries(getModel.getKey(projectId)),
      queryClient.invalidateQueries(getStiffeningProposal.getKey(projectId)),
      queryClient.invalidateQueries(getStiffeningIntervals.getKey(projectId)),
      queryClient.invalidateQueries(getTensileTransmissionGraph.getKey(projectId)),
    ])
    invalidateResults(projectId as string)
    return newWall
  }

  const handleDeleteWall = async (wallGuid: string) => {
    await deleteWall.request(projectId as string, wallGuid)
    removeWall(wallGuid)
    await Promise.all([
      queryClient.invalidateQueries(getModel.getKey(projectId)),
      queryClient.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
      queryClient.invalidateQueries(getModel.getKey(projectId)),
      queryClient.invalidateQueries(getStiffeningProposal.getKey(projectId)),
      queryClient.invalidateQueries(getStiffeningIntervals.getKey(projectId)),
      queryClient.invalidateQueries(getTensileTransmissionGraph.getKey(projectId)),
    ])
    invalidateResults(projectId as string)
  }

  return (
    <Box>
      <TabContext value={selectedTab}>
        {!hideTabs && (
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                fontSize: 12,
                paddingX: ({ spacing }) => spacing(1.5),
              },
            }}
          >
            <TabList
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={(_: any, value: any) => setActiveTab(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab
                value={wallTabs.geometry.tab}
                label={wallTabs.geometry.label}
                data-cy="tab-geometry"
              />
              <Tab
                value={wallTabs.verticalTransmission.tab}
                label={wallTabs.verticalTransmission.label}
                data-cy="tab-wall-vertical-transmission"
              />
              <Tab
                value={wallTabs.connector.tab}
                label={wallTabs.connector.label}
                data-cy="tab-connector"
              />
            </TabList>
          </Box>
        )}
        <StyledTabPanel value={wallTabs.connector.tab}>
          <TensileTransmitterList
            selectedElement={selectedElementGuid}
            onSave={saveGraph}
            onRecalculate={() => recalculateTargets()}
            isRecalculating={isRecalculating}
            isLoading={isSavingGraph}
          />
        </StyledTabPanel>
        <StyledTabPanel value={wallTabs.verticalTransmission.tab}>
          <VerticalTransmitterList
            selectedElement={selectedElementGuid}
            verticalTransmitter={verticalTransmitter}
            setVerticalTransmitter={setVerticalTransmitter}
          />
        </StyledTabPanel>
        <StyledTabPanel value={wallTabs.geometry.tab}>
          <Stack direction="column" spacing={0.5}>
            {wall && (
              <WallGeometryForm
                wall={wall}
                handleSubmit={(submittedWall, dependentWalls) =>
                  handleUpdate({ submittedWall, dependentWalls })
                }
                handleDelete={() => handleDelete(selectedElementGuid)}
                isDeleting={isDeleting}
                isSubmitting={isUpdating}
                sceneEditEnabled={sceneEditEnabled}
                handleSceneEditSubmit={
                  saveOnSceneEdit
                    ? (submittedWall, dependentWalls) =>
                        handleUpdate({ submittedWall, dependentWalls, fromSceneEdit: true })
                    : undefined
                }
                onClose={onClose}
              />
            )}

            {showStructuralSections && (
              <CollapsibleAssemblyBoxSection defaultExpanded={true}>
                <Stack direction="column" spacing={1}>
                  <AssemblyAssignmentForm
                    elementType={assemblyElementType}
                    selectedElements={[selectedElementGuid]}
                    onClose={onClose}
                    showSelectedElementsNumber={false}
                    autoFocus={false}
                  />
                </Stack>
              </CollapsibleAssemblyBoxSection>
            )}
            <CollapsibleStiffeningBoxSection
              defaultExpanded={true}
              isEmpty={intervals.length === 0}
            >
              <StiffeningElementList
                intervals={intervals}
                onSave={() => saveIntervals(stiffeningIntervals)}
                isLoading={isSavingIntervals}
                selectedStiffeningElement={selectedStiffeningElements?.[0] || undefined}
                setlectStiffeningElement={setStiffeningElement}
              />
            </CollapsibleStiffeningBoxSection>

            {showStructuralSections && (
              <CollapsibleWallRipsBoxSection defaultExpanded={true}>
                <Stack direction="column" spacing={1} justifyContent="space-evenly">
                  <MultiEditWallRipsEdges
                    elementGuids={[selectedElementGuid]}
                    hideElementColumn
                    defaultCreation={false}
                    wallLength={wallLength}
                  />
                </Stack>
              </CollapsibleWallRipsBoxSection>
            )}

            {wall && (
              <CollapsibleOpeningsBoxSection defaultExpanded={false}>
                <WallOpeningsSection
                  wall={wall}
                  mode="3D"
                  onEditInTopView={onEditOpeningInTopView}
                />
              </CollapsibleOpeningsBoxSection>
            )}

            {showStructuralSections && wall && !wall.is_local && (
              <CollapsibleSettingsBoxSection defaultExpanded={false}>
                <PlanSettingsForm element={wall} />
              </CollapsibleSettingsBoxSection>
            )}

            <CollapsibleSplitBoxSection label="Teilen" defaultExpanded={false}>
              <SplitWallForm
                element_guid={selectedElementGuid}
                handleCreate={handleCreateWall}
                handleDelete={handleDeleteWall}
              />
            </CollapsibleSplitBoxSection>
            {wall?.storey && (
              <CollapsibleCopyBoxSection
                label="Kopieren"
                defaultExpanded={false}
                dataCy="wall-form-copy-to-storey"
              >
                <CopyToOtherStoreys selectedElements={[selectedElementGuid]} storey={wall.storey} />
              </CollapsibleCopyBoxSection>
            )}
            {showStructuralSections && (
              <CollapsibleDocumentationBoxSection defaultExpanded={false}>
                <DocumentationItem itemGuid={selectedElementGuid} />
              </CollapsibleDocumentationBoxSection>
            )}

            {showStructuralSections && (
              <CollapsibleProblemsBoxSection
                elementGuid={selectedElementGuid}
                defaultExpanded={true}
              />
            )}

            {showStructuralSections && wall?.storey && (
              <ConfiguredWallAlignmentForm
                key={selectedElementGuid}
                wallGuid={selectedElementGuid}
                wallStorey={wall.storey}
              />
            )}
          </Stack>
        </StyledTabPanel>
      </TabContext>
    </Box>
  )
}

export default WallForm
